import * as React from "react";
import {
  makeStyles,
  Theme,
  createStyles,
  Grid,
  useTheme,
  ButtonGroup,
  Button,
  Typography,
  Card,
  Hidden,
  IconButton,
  Tooltip,
  DialogContent,
  DialogActions,
  Switch,
} from "@material-ui/core";
import indexOf from "lodash/indexOf";
import BaseChart from "../../../components/Charts/BaseChart";
import {
  BOXPLOT_HEATMAP_CHART_SLOT_CATEGORIES,
  BOXPLOT_HEATMAP_CHART_WEEK_DAYS_CATEGORIES,
  BOXPLOT_HEATMAP_CHART_WEEK_DAYS_CATEGORIES_PT_BR,
} from "../../../core/constants/businnesConstants";
import {
  IConsumptionReading,
  IConsumptionResult,
  ILevels,
} from "../../../core/contracts/IConsumption";
import { useHubEventDateWindow } from "../../../core/hooks/useHubHooks";
import { ConsumptionReadings } from "../../../core/models/ConsuptionReadings";
import InstallationModel from "../../../core/models/Installation";
import { TimeseriesData } from "../../../core/models/TimeSeriesData";
import {
  EVENT_DARK_MODE,
  EVENT_INSTALLATION_CONSUMPTION_DATE_CHANGE,
  EVENT_INSTALLATION_CONSUMPTION_NORMALIZE_DATA_CONFIG_CHANGE,
  EVENT_REFRESH_INSTALLATION_DETAIL_PAGE,
} from "../../../Events";
import { FETCH_INSTALLATION_CONSUMPTION } from "../../../Fedx";
import {
  buildBoxPlotChartOptions,
  buildGaugePlot,
  buildHeatMapChartOptions,
  buildTimeAreaChartOptions,
} from "../../../helpers/chartHelpers";
import {
  aggregateConsumptionReadingsDataByDay,
  calculateCoefVariation,
  calculateMean,
  calculateStdDv,
  calculateSum,
} from "../../../helpers/consumptionStatisticalHelpers";
import { formatValue, isArrayValid } from "../../../helpers/utility";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { InstallationConsumptionServiceFetchParams } from "../../../services/InstallationConsumption";
import ButtonSwitch from "../../../components/ButtonSwitch";
import HelpIcon from "../../../components/HelpIcon";
import { EMPTY_STRING_LABEL } from "../../../core/constants/appGlobals";
import groupBy from "lodash/groupBy";
import { interquartileRange, quantile } from "../../../helpers/simple-statistics-required";
import { GridSkeleton } from "../../../components/MaterialSkeleton";
import clsx from "clsx";
import {
  STD_DEV_HELP_TEXT,
  COEF_VARIATION_HELP_TEXT,
  HEAT_MAP_HELP_TEXT,
  BOX_PLOT_HELP_TEXT,
  CONSUMPTION_ACUMULATED_HELP_TEXT,
  CONSUMPTION_HELP_TEXT,
} from "../../../core/constants/tooltipHelpersText";
import InfoIcon from "@material-ui/icons/Info";
import SimpleDialog from "../../../components/SimpleDialog";
import { consumptionData } from "../../../core/models/ConsumptionData";
import { Chart, Point, PointerEventObject } from "highcharts";
import useConstructor from "../../../core/hooks/useConstructor";
import { LocalCache } from "../../../helpers/cache";
import { calculateDays, formatDateTime } from "../../../helpers/datetime";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loadingRoot: {
      height: "100%",
    },

    mainChartTitle: {
      display: "flex",
      justifyContent: "space-between",
      padding: theme.spacing(1, 3, 0.125, 3),

      [theme.breakpoints.down("xs")]: {
        alignItems: "center",
      },
    },

    mainChartWrapper: {
      "position": "relative",
      "marginBottom": theme.spacing(2),

      "& .title": {
        color: theme.palette.primary.main,
      },
    },

    statisticalInformationWrapper: {
      "display": "flex",
      "columnGap": theme.spacing(3),
      "flexWrap": "wrap",
      "right": 0,

      [theme.breakpoints.down("xs")]: {
        rowGap: `${theme.spacing(2)}px`,
      },

      "marginBottom": 10,

      "& .card": {
        "display": "flex",
        "flexDirection": "column",
        "alignItems": "center",
        "paddingBottom": 5,

        "height": "100%",

        "& .row": {
          "display": "flex",
          "alignItems": "center",
          "gridColumnGap": theme.spacing(1),
          "padding": theme.spacing(0.125, 1),
          "color": theme.palette.text.primary,
          "position": "relative",

          "& .value": {
            fontWeight: "bold",
          },

          "& .average": {
            position: "absolute",
            top: "37px",
            [theme.breakpoints.up("md")]: {
              fontSize: "40px !important",
            },
            [theme.breakpoints.down("md")]: {
              fontSize: 35,
            },
          },

          "& .uom": {
            color: theme.customPaper.background.contrast,
            width: "100%",
            position: "absolute",
            left: 0,
            bottom: 0,
          },
        },
        "& .spacing": {
          width: "100%",
          display: "flex",
          textAlign: "center",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%",
          [theme.breakpoints.down("md")]: {
            minHeight: "150px",
          },
        },
      },
    },

    chartTitleButtonWrapper: {
      "display": "flex",
      "justifyContent": "space-between",
      "padding": theme.spacing(2),
      "alignItems": "center",
      "flexWrap": "wrap",

      "& .title": {
        color: theme.palette.primary.main,
        display: "flex",
        columnGap: theme.spacing(2),
        alignItems: "flex-start",
      },
    },

    cardTitle: {
      display: "flex",
      padding: 16,
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      fontSize: 12,
      borderBottom: "1px solid #e6e6e6",
      textAlign: "center",
      fontWeight: "bold",
      zIndex: 12,
    },

    altPadding: {
      padding: theme.spacing(0.75, 2),
    },

    groupButtonWrapper: {
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "left",

      "& .mini-label": {
        color: theme.palette.text.primary,
        fontSize: "0.6rem",
      },

      "& .selected": {
        background:
          theme.palette.type == "dark"
            ? theme.customPaper.background.cardBackgroundLightContrast
            : "#6E7878",
        color: theme.palette.common.white,
      },
    },
  })
);

const yCategoriesToCompare = BOXPLOT_HEATMAP_CHART_WEEK_DAYS_CATEGORIES.map((x) =>
  x.toLocaleLowerCase()
);

type InstallationConsumptionProps = {
  installation?: InstallationModel;
};

type boxPlotAggregation = "all" | "weekday" | "slot";

function truncateAt0(value: number) {
  if (value < 0) {
    return 0;
  }
  return value;
}

function calculateWithTruncateAt0(value: number, shouldNotTruncate: boolean) {
  if (!shouldNotTruncate) {
    return truncateAt0(value);
  }

  return value;
}

function calculateQuartilesAndOutliers(data: consumptionData[], enableNegativeValues = false) {
  const values = data.map((x) => x.level).sort();

  const q1 = calculateWithTruncateAt0(quantile(values, 0.25), enableNegativeValues);
  const q2 = calculateWithTruncateAt0(quantile(values, 0.5), enableNegativeValues);
  const q3 = calculateWithTruncateAt0(quantile(values, 0.75), enableNegativeValues);

  const iqr = interquartileRange(values);

  const minThreshold = calculateWithTruncateAt0(q1 - 1.5 * iqr, enableNegativeValues);
  const maxThreshold = calculateWithTruncateAt0(q3 + 1.5 * iqr, enableNegativeValues);

  const outliers = data?.filter((d) => d.level > maxThreshold || d.level < minThreshold);

  return {
    quartile: [minThreshold, q1, q2, q3, maxThreshold],
    outliers,
  };
}

function calculateGroupQuartilesAndOutLiers(groupedData: Record<string, consumptionData[]>) {
  let dataToReturn: number[][] = [];
  let outliers: (number | consumptionData)[][] = [];
  let idx = 0;
  for (const key in groupedData) {
    const currentData = groupedData[key];
    const statistics = calculateQuartilesAndOutliers(currentData);

    const currentQuartile = [statistics.quartile];
    const currentOutliers = statistics.outliers;
    dataToReturn = [...dataToReturn, ...currentQuartile];
    outliers = [...outliers, ...currentOutliers.map((x) => [idx, x])];
    idx++;
  }

  return {
    data: dataToReturn,
    outliers,
  };
}

function InstallationConsumption({ installation }: InstallationConsumptionProps) {
  const [isDark, setIsDark] = React.useState<boolean>(false);

  const lcache = new LocalCache();

  const getFromCache = () => {
    const prefersDark = lcache.get<boolean>("prefersDarkMode") || false;
    setIsDark(prefersDark);
  };

  useConstructor(async () => {
    getFromCache();
  });

  useHubXSubscribe(EVENT_DARK_MODE, (isDark: boolean) => {
    setIsDark(isDark);
  });

  const [loading, setLoading] = React.useState(false);
  const [
    installationConsumptionData,
    setInstallationConsumptionData,
  ] = React.useState<ConsumptionReadings | null>(null);
  const [dailyConsumptionData, setDailyConsumptionData] = React.useState<
    IConsumptionReading[] | null
  >(null);

  const [dailyConsumptionLevelData, setDailyConsumptionLevelData] = React.useState<
    Record<string, Record<string, ILevels | undefined>[]>[]
  >([]);

  const [useNormalizedData, setUseNormalizedData] = React.useState(true);

  // false = slot, true = daily
  const [heatMapChartSlotOrDailyData, setHeatMapChartSlotOrDailyData] = React.useState(false);

  const [boxPlotAggregation, setBoxPlotAggregation] = React.useState<boxPlotAggregation>("all");

  const [mobileDialogOpen, setMobileDialogOpen] = React.useState<boolean>(false);

  const [onHour, setOnHour] = React.useState<boolean>(false);

  const filterInterval = useHubEventDateWindow(EVENT_INSTALLATION_CONSUMPTION_DATE_CHANGE, true);

  const classes = useStyles();
  const hub = useHubx();
  const theme = useTheme();

  useHubXSubscribe(
    EVENT_INSTALLATION_CONSUMPTION_NORMALIZE_DATA_CONFIG_CHANGE,
    setUseNormalizedData
  );
  useHubXSubscribe(EVENT_REFRESH_INSTALLATION_DETAIL_PAGE, fetchData);

  async function fetchData() {
    if (installation?.installation_id) {
      setLoading(true);

      try {
        const fetchParams: InstallationConsumptionServiceFetchParams = {
          installation_id: installation?.installation_id,
          startDate: filterInterval?.start || undefined,
          endDate: filterInterval?.end || undefined,
        };

        if (fetchParams.startDate && fetchParams.endDate) {
          const APIInstallationResponse: ConsumptionReadings = await hub.request(
            FETCH_INSTALLATION_CONSUMPTION,
            fetchParams
          );

          if (APIInstallationResponse?.readings) {
            const prevResult = APIInstallationResponse.readings as IConsumptionReading[];
            const resultAggregation: IConsumptionResult = aggregateConsumptionReadingsDataByDay(
              prevResult
            );
            setDailyConsumptionData(resultAggregation.readings);
            setDailyConsumptionLevelData(resultAggregation.levels);
            setInstallationConsumptionData(APIInstallationResponse);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("InstallationConsumption → fetchData → error", error);
      }

      setLoading(false);
    }
  }

  React.useEffect(() => {
    const doFetch = async () => await fetchData();
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installation?.installation_id, filterInterval]);

  const dailyMean = React.useMemo(() => {
    console.log(dailyConsumptionData);
    if (dailyConsumptionData) {
      const today = new Date(new Date().setHours(0, 0, 0, 0));
      const filteredToday = dailyConsumptionData.filter((x) => new Date(x.date) < today);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const data = filteredToday.map((x) => (useNormalizedData ? x.level : x.raw_level));
      const mean = calculateMean(data);
      const stdDev = calculateStdDv(data);
      const CoefVariation = calculateCoefVariation(data);

      let sum = 0;
      for (let index = 0; index < data.length; index++) {
        sum += data[index];
      }

      const filteredDates = filteredToday.map((x) => new Date(x.date));
      const start = filteredDates.reduce(function (a, b) {
        return a < b ? a : b;
      });
      const end = filteredDates.reduce(function (a, b) {
        return a > b ? a : b;
      });
      if (end.getHours() === 0) {
        end.setHours(23);
        end.setMinutes(59);
        end.setSeconds(59);
      }

      return {
        mean,
        stdDev,
        CoefVariation,
        sum,
        start,
        end,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyConsumptionData, useNormalizedData]);

  const consumptionTimeSeries = React.useMemo(() => {
    let graphData: TimeseriesData[] = [];
    // let graphPointValues: TimeseriesData[] = [];
    let yThresholds;

    if (dailyConsumptionData) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      graphData = dailyConsumptionData.map((d: IConsumptionReading) => {
        return [new Date(d.date), useNormalizedData ? d.level : d.raw_level];
      });
    }

    if (dailyMean) {
      yThresholds = [
        {
          id: `consumption_chart_mean_threshold`,
          value: dailyMean.mean,
          color: theme.palette.text.secondary,
          HTMLLabel: `Consumo médio (${formatValue(dailyMean.mean, 2)} ${
            installation?.product_uom
              ? installation.product_uom == "M3"
                ? "m³"
                : installation.product_uom
              : "u.m."
          }/dia)`,
        },
        {
          id: `consumption_chart_mean_1.2_threshold`,
          value: dailyMean.mean * 0.8,
          color: theme.palette.primary.main,
          HTMLLabel: "Desvio (-20%)",
        },
        {
          id: `consumption_chart_mean_0.8_threshold`,
          value: dailyMean.mean * 1.2,
          color: theme.palette.primary.main,
          HTMLLabel: "Desvio (+20%)",
        },
      ];
    }

    const ret = buildTimeAreaChartOptions(
      graphData,
      "",
      "Consumo",
      `${
        installation?.product_uom
          ? `(${installation.product_uom == "M3" ? "m³" : installation.product_uom}/dia)`
          : ""
      }`,
      yThresholds,
      undefined,
      true,
      true,
      useNormalizedData,
      dailyConsumptionLevelData,
      undefined,
      undefined,
      undefined,
      isDark,
      true
    );

    return {
      ...ret,
      chart: { ...ret.chart, height: 275 },
      legend: { enabled: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyConsumptionData, dailyConsumptionLevelData, dailyMean, useNormalizedData, isDark]);

  const consumptionGauge = React.useMemo(() => {
    const value = dailyMean ? Math.floor(dailyMean?.CoefVariation) : 0;
    const ret = buildGaugePlot([value], isDark);

    return {
      ...ret,
      chart: { ...ret.chart, height: 200 },
      legend: { enabled: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyMean, isDark]);

  const heatmapChartOptions = React.useMemo(() => {
    const tempMap: Record<string, number> = {};
    const tempMapCount: Record<string, number> = {};
    let data: number[][] = [];
    if (heatMapChartSlotOrDailyData && isArrayValid(installationConsumptionData?.readings)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      installationConsumptionData!.readings?.forEach((d) => {
        const useIdentifier = `${indexOf(
          BOXPLOT_HEATMAP_CHART_SLOT_CATEGORIES,
          d.slot.toString()
        )}-${indexOf(yCategoriesToCompare, d.day_of_week.toLocaleLowerCase())}`;

        if (!tempMap[useIdentifier]) {
          tempMap[useIdentifier] = useNormalizedData ? d.level : d.raw_level;
          tempMapCount[useIdentifier] = 1;
        } else {
          tempMap[useIdentifier] += useNormalizedData ? d.level : d.raw_level;
          tempMapCount[useIdentifier] += 1;
        }
      });

      data = Object.keys(tempMap).map((key) => {
        const value = tempMap[key];
        const [x, y] = key.split("-");
        return [Number(x), Number(y), value];
      });
    }

    if (!heatMapChartSlotOrDailyData && isArrayValid(dailyConsumptionData)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dailyConsumptionData!.forEach((d) => {
        const useIdentifier = indexOf(yCategoriesToCompare, d.day_of_week.toLocaleLowerCase());
        if (!tempMap[useIdentifier]) {
          tempMap[useIdentifier] = useNormalizedData ? d.level : d.raw_level;
          tempMapCount[useIdentifier] = 1;
        } else {
          tempMap[useIdentifier] += useNormalizedData ? d.level : d.raw_level;
          tempMapCount[useIdentifier] += 1;
        }
      });

      data = Object.keys(tempMap).map((key) => {
        const value = tempMap[key];
        const count = tempMapCount[key];
        return [0, Number(key), value / count];
      });
    }

    const ret = buildHeatMapChartOptions(
      heatMapChartSlotOrDailyData ? BOXPLOT_HEATMAP_CHART_SLOT_CATEGORIES : ["0"],
      BOXPLOT_HEATMAP_CHART_WEEK_DAYS_CATEGORIES_PT_BR,
      data,
      "",
      "Consumo",
      isDark
    );

    return { ...ret, chart: { ...ret.chart, height: 320 } };
  }, [
    dailyConsumptionData,
    heatMapChartSlotOrDailyData,
    installationConsumptionData,
    useNormalizedData,
  ]);

  const boxPlotChartOptions = React.useMemo(() => {
    if (isArrayValid(installationConsumptionData?.readings)) {
      let xCategories = ["0"];
      let data: number[][] = [];
      let outliers: (number | consumptionData)[][] = [];

      const useConsumptionData: consumptionData[] =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        installationConsumptionData!.readings?.map((x) => {
          const { level, raw_level, ...others } = x;

          return {
            level: useNormalizedData ? level : raw_level,
            ...others,
          };
        }) || [];

      let groupedData: consumptionData[] | Record<string, consumptionData[]> = [];
      let statisticalData: { data: number[][]; outliers: (number | consumptionData)[][] } = {
        data: [],
        outliers: [],
      };
      switch (boxPlotAggregation) {
        case "all":
          const statistics = calculateQuartilesAndOutliers(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            dailyConsumptionData!
          );

          xCategories = ["Geral"];
          data = [statistics.quartile];
          outliers = statistics.outliers.map((x) => [0, x]);

          break;
        case "slot":
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          groupedData = groupBy(useConsumptionData!, "slot");
          xCategories = BOXPLOT_HEATMAP_CHART_SLOT_CATEGORIES;
          statisticalData = calculateGroupQuartilesAndOutLiers(groupedData);
          data = statisticalData.data;
          outliers = statisticalData.outliers;
          break;

        case "weekday":
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          groupedData = groupBy(dailyConsumptionData!, "day_of_week");
          xCategories = BOXPLOT_HEATMAP_CHART_WEEK_DAYS_CATEGORIES_PT_BR;
          statisticalData = calculateGroupQuartilesAndOutLiers(groupedData);
          data = statisticalData.data;
          outliers = statisticalData.outliers;
          break;

        default:
          // do nothing
          break;
      }

      const ret = buildBoxPlotChartOptions(
        data,
        xCategories,
        outliers,
        "Consumo",
        installation?.product_uom == "M3" ? "m³" : installation?.product_uom,
        undefined,
        isDark
      );

      return { ...ret, chart: { ...ret.chart, height: 320 } };
    }
  }, [
    boxPlotAggregation,
    dailyConsumptionData,
    installation,
    installationConsumptionData,
    useNormalizedData,
  ]);

  const afterChartCreated = (chart: Chart) => {
    if (chart.series && chart.series[0]) {
      chart.series[0].data.forEach(function (d: Point) {
        const level = (dailyConsumptionLevelData[d.x] as unknown) as Record<
          string,
          ILevels | undefined
        >;

        if (level) {
          const keys = Object.keys(level);
          if (keys.length < 4) {
            d.update(
              {
                color: "red",
              },
              false,
              false
            );
          }
        }
      });
    }
  };

  // calculate dailyMean in m³/h considering the 6h intervals received.
  const dailyReadingInterval = installationConsumptionData?.readings?.length * 6; // each reading has an interval of 6h

  const renderStatisticalCardsInformation = () => {
    return (
      <div className={classes.statisticalInformationWrapper}>
        {/* <Hidden xsDown>asdasdasd</Hidden> */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card className="card" elevation={2}>
              <Typography gutterBottom variant="h5" component="h2" className={classes.cardTitle}>
                CONSUMO MÉDIO <HelpIcon tooltip={CONSUMPTION_HELP_TEXT} />
              </Typography>
              <div className="row spacing">
                <span className="value average" style={{ display: "block" }}>
                  {onHour
                    ? dailyMean?.mean
                      ? formatValue(dailyMean?.sum / dailyReadingInterval, 2)
                      : EMPTY_STRING_LABEL
                    : formatValue(dailyMean?.sum / (dailyReadingInterval / 24), 2)}
                </span>
                <span className="uom">
                  {installation?.product_uom
                    ? `${installation.product_uom == "M3" ? "m³" : installation.product_uom}/${
                        onHour ? "h" : "dia"
                      }`
                    : EMPTY_STRING_LABEL}
                  <Switch checked={onHour} onChange={() => setOnHour(!onHour)} />
                </span>
              </div>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card className="card" elevation={2}>
              <div className="row">
                <Typography gutterBottom variant="h5" component="h2" className={classes.cardTitle}>
                  COEFICIENTE DE VARIAÇÃO <HelpIcon tooltip={COEF_VARIATION_HELP_TEXT} />
                </Typography>
              </div>
              <div className="row" style={{ marginTop: "-70px" }}>
                <BaseChart
                  maxHeight={200}
                  options={consumptionGauge as Highcharts.Options}
                  allowChartUpdate
                  callback={afterChartCreated}
                />
              </div>
              <div className="row">
                <Typography>Desvio Padrão:</Typography>
                <span className="value">{formatValue(dailyMean?.stdDev, 2)}</span>
                <span>
                  {installation?.product_uom
                    ? `${installation.product_uom == "M3" ? "m³" : installation.product_uom}/dia`
                    : EMPTY_STRING_LABEL}
                </span>
                <HelpIcon tooltip={STD_DEV_HELP_TEXT} />
              </div>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card className="card" elevation={2}>
              <Typography gutterBottom variant="h5" component="h2" className={classes.cardTitle}>
                CONSUMO ACUMULADO <HelpIcon tooltip={CONSUMPTION_ACUMULATED_HELP_TEXT} />
              </Typography>

              <div className="row spacing">
                <span className="value average" style={{ fontSize: 35, display: "block" }}>
                  {dailyMean?.sum ? formatValue(dailyMean.sum, 2) : EMPTY_STRING_LABEL}
                  {installation?.product_uom
                    ? `${installation.product_uom == "M3" ? "m³" : installation.product_uom}`
                    : EMPTY_STRING_LABEL}
                </span>
                <span className="uom" style={{ fontSize: 16, marginBottom: "5px" }}>
                  {dailyMean
                    ? formatDateTime(dailyMean.start || undefined, "MMM, d") +
                      " - " +
                      formatDateTime(dailyMean.end || undefined, "MMM, d") +
                      " (" +
                      calculateDays(dailyMean.start, dailyMean.end) +
                      ")"
                    : null}
                </span>
              </div>
            </Card>
          </Grid>{" "}
        </Grid>
      </div>
    );
  };

  if (loading) {
    return (
      <Grid className={classes.loadingRoot} container direction="column">
        <Grid item xs>
          <GridSkeleton />
        </Grid>

        <Grid item container spacing={5} xs>
          <Grid item xs>
            <GridSkeleton />
          </Grid>
          <Grid item xs>
            <GridSkeleton />
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      {renderStatisticalCardsInformation()}
      <Card className={classes.mainChartWrapper}>
        <div className={classes.mainChartTitle}>
          <Typography className={"title"} component="h5">
            <strong>Série Temporal Consumo</strong>
          </Typography>
        </div>

        <BaseChart
          maxHeight={350}
          options={consumptionTimeSeries as Highcharts.Options}
          allowChartUpdate
          callback={afterChartCreated}
        />
      </Card>

      <Grid container spacing={2}>
        <Grid item sm={6} xs={12}>
          <Card>
            <div className={classes.chartTitleButtonWrapper}>
              <div className="title">
                <Typography component="h5">
                  <strong>Mapa de Calor</strong>
                </Typography>
                <HelpIcon tooltip={HEAT_MAP_HELP_TEXT} />
              </div>
              <ButtonSwitch
                label={"Pontos:"}
                offLabel="Diário"
                onLabel="Slot"
                onClick={(newValue: boolean) => setHeatMapChartSlotOrDailyData(newValue)}
                value={heatMapChartSlotOrDailyData}
              />
            </div>
            <BaseChart maxHeight={385} allowChartUpdate options={heatmapChartOptions} />
          </Card>
        </Grid>
        <Grid item sm={6} xs={12}>
          <Card>
            <div className={clsx(classes.chartTitleButtonWrapper, classes.altPadding)}>
              <div className="title">
                <Typography component="h5">
                  <strong>Boxplot</strong>
                </Typography>
                <HelpIcon tooltip={BOX_PLOT_HELP_TEXT} />
              </div>
              <div className={classes.groupButtonWrapper}>
                <span className="mini-label">Tipo:</span>
                <ButtonGroup disableElevation>
                  <Button
                    className={clsx(boxPlotAggregation === "all" && "selected")}
                    onClick={() => setBoxPlotAggregation("all")}
                  >
                    Geral
                  </Button>
                  <Button
                    className={clsx(boxPlotAggregation === "weekday" && "selected")}
                    onClick={() => setBoxPlotAggregation("weekday")}
                  >
                    Semanal
                  </Button>
                  <Button
                    className={clsx(boxPlotAggregation === "slot" && "selected")}
                    onClick={() => setBoxPlotAggregation("slot")}
                  >
                    Slot
                  </Button>
                </ButtonGroup>
              </div>
            </div>
            <BaseChart allowChartUpdate maxHeight={385} options={boxPlotChartOptions} />
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}

export default InstallationConsumption;
